import { useCallback, useEffect, useState } from 'react'

// Per-device preference for whether the chat UI substitutes Stream Chat's
// auto-translated text (`message.i18n.{lang}_text`) over the original
// `message.text`. Toggling this does NOT touch the Stream Chat server-side
// auto-translation feature itself — it only controls what this client renders.
const STORAGE_KEY = 'gologic.chat.translateMessages'

const readInitial = (): boolean => {
  if (typeof window === 'undefined') return true
  const v = window.localStorage.getItem(STORAGE_KEY)
  // Default: ON. Only an explicit "false" disables.
  return v === null ? true : v !== 'false'
}

let listeners: Array<(v: boolean) => void> = []
let current = readInitial()

const broadcast = (next: boolean) => {
  current = next
  for (const fn of listeners) fn(next)
}

export const useTranslateMessages = () => {
  const [enabled, setEnabled] = useState<boolean>(current)

  useEffect(() => {
    const listener = (v: boolean) => setEnabled(v)
    listeners.push(listener)
    return () => {
      listeners = listeners.filter((l) => l !== listener)
    }
  }, [])

  const toggle = useCallback(() => {
    const next = !current
    try {
      window.localStorage.setItem(STORAGE_KEY, next ? 'true' : 'false')
    } catch {
      // localStorage may be unavailable in private-mode browsers; ignore.
    }
    broadcast(next)
  }, [])

  return { enabled, toggle }
}
